import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "./../lib/api";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext(null);
const GUEST_KEY = "vortex_wishlist_guest_v1";

const readGuest = () => {
    try { return JSON.parse(localStorage.getItem(GUEST_KEY) || "[]"); }
    catch { return []; }
};
const writeGuest = (slugs) => localStorage.setItem(GUEST_KEY, JSON.stringify(slugs));

export const WishlistProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [slugs, setSlugs] = useState(() => readGuest());
    const [ready, setReady] = useState(false);
    const [pendingMerge, setPendingMerge] = useState(null);

    // Hydrate from server when logged in; offer merge if guest list is non-empty
    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            setSlugs(readGuest());
            setReady(true);
            return;
        }
        (async () => {
            try {
                const { data } = await api.get("/me/wishlist");
                const serverSlugs = data.slugs || [];
                const guestSlugs = readGuest();
                const toMerge = guestSlugs.filter((s) => !serverSlugs.includes(s));
                if (toMerge.length > 0) {
                    setPendingMerge(toMerge);
                }
                setSlugs(serverSlugs);
            } catch {
                // ignore
            }
            setReady(true);
        })();
    }, [user, authLoading]);

    // Persist guest changes locally
    useEffect(() => {
        if (!user && ready) writeGuest(slugs);
    }, [slugs, user, ready]);

    const has = useCallback((slug) => slugs.includes(slug), [slugs]);

    const add = async (slug, productName) => {
        if (slugs.includes(slug)) return;
        if (user) {
            try {
                const { data } = await api.post("/me/wishlist", { slug });
                setSlugs(data.slugs);
                toast.success(`Added to Wishlist`);
            } catch {
                toast.error("Could not add to wishlist");
            }
        } else {
            setSlugs((p) => [...p, slug]);
            toast.success(`Added to Wishlist`);
        }
        // Optional product-name suppression — already in toast above
        void productName;
    };

    const remove = async (slug) => {
        if (user) {
            try {
                const { data } = await api.delete(`/me/wishlist/${encodeURIComponent(slug)}`);
                setSlugs(data.slugs);
            } catch {
                toast.error("Could not remove");
            }
        } else {
            setSlugs((p) => p.filter((s) => s !== slug));
        }
    };

    const toggle = (slug, productName) => {
        if (has(slug)) remove(slug);
        else add(slug, productName);
    };

    const mergeGuestNow = async () => {
        if (!user || !pendingMerge || pendingMerge.length === 0) return;
        try {
            const { data } = await api.post("/me/wishlist/merge", { slugs: pendingMerge });
            setSlugs(data.slugs);
            writeGuest([]);
            setPendingMerge(null);
            toast.success(`Merged ${pendingMerge.length} item${pendingMerge.length === 1 ? "" : "s"} into your account`);
        } catch {
            toast.error("Merge failed");
        }
    };

    const dismissMerge = () => {
        setPendingMerge(null);
        writeGuest([]);
    };

    return (
        <WishlistContext.Provider value={{ slugs, has, add, remove, toggle, pendingMerge, mergeGuestNow, dismissMerge, count: slugs.length, ready }}>
            {children}
        </WishlistContext.Provider>
    );
};

export const useWishlist = () => {
    const ctx = useContext(WishlistContext);
    if (!ctx) throw new Error("useWishlist must be used inside WishlistProvider");
    return ctx;
};
